import { turbine } from "neql"
import { useCallback, useState } from "react"
import { useDispatch } from "react-redux"
import { dirtinessChanged } from "../Actions/Client/InputRender/InputRenderActions"
import { failMessage } from "../Actions/Client/MessagesActions"
import { defaultTutorial } from "../Models/API/QualifierModel"
import { useNeqlContextProvider } from "./useNeqlContextProvider"

export interface TrainingTutorialVariables {
    id: string
    tutorialCorrectAnswer?: turbine.TrainingCorrectAnswer
    cutoffMetricName: string
    cutoffOperator?: turbine.MetricOperator
    cutoffValue?: number
    cutoffMetricDisplayName: string
}

export const useQualifierTutorialCreate = (
    appID: string,
    quaifierName: string = "Tutorial",
    reloadTutorials: () => void
): {
    sampleTutorial?: turbine.TrainingTutorial
    addSampleTutorial: () => void
    createOrUpdateTutorial: (
        params: TrainingTutorialVariables
    ) => Promise<boolean>
    isUpdatingTutorial: boolean
} => {
    const clientContext = useNeqlContextProvider("turbine")
    const dispatch = useDispatch()

    const [sampleTutorial, setSampleTutorial] =
        useState<turbine.TrainingTutorial | undefined>()

    const [createTutorial, { loading: tutorialCreationLoading }] =
        turbine.useCreateTrainingTutorialMutation({
            client: clientContext
        })

    const [updateTutorial, { loading }] =
        turbine.useUpdateTrainingTutorialMutation({
            client: clientContext
        })
    const addSampleTutorial = useCallback(() => {
        setSampleTutorial({
            ...defaultTutorial,
            taskInfo: {
                ...defaultTutorial.taskInfo,
                title: `Tutorial: ${quaifierName}`
            },
            appUuid: appID,
            state: turbine.TrainingApplicationState.Active,
            trainingState: turbine.TrainingState.Creating
        })
        dispatch(dirtinessChanged(true))
    }, [appID, dispatch, quaifierName])

    const createOrUpdateTutorial = useCallback(
        async ({
            id,
            tutorialCorrectAnswer,
            cutoffValue,
            cutoffMetricName,
            cutoffMetricDisplayName,
            cutoffOperator
        }: TrainingTutorialVariables) => {
            try {
                if (sampleTutorial !== undefined && sampleTutorial.id === id) {
                    const createdTutorial = await createTutorial({
                        variables: {
                            createTrainingTutorialAppUuid:
                                sampleTutorial.appUuid,
                            createTrainingTutorialCorrectAnswer:
                                tutorialCorrectAnswer ??
                                turbine.TrainingCorrectAnswer.Skip,
                            createTrainingTutorialCutoffMetricName:
                                cutoffMetricName,
                            createTrainingTutorialCutoffOperator:
                                cutoffOperator ??
                                turbine.MetricOperator.GreaterThan,
                            createTrainingTutorialCutoffValue:
                                cutoffValue ?? 0.9,
                            createTrainingTutorialTitle:
                                sampleTutorial.taskInfo?.title ??
                                `Tutorial: ${quaifierName}`,
                            createTrainingTutorialDescription:
                                sampleTutorial.taskInfo?.description ??
                                "A short task summary for the Human Worker goes here...",

                            createTrainingTutorialCutoffMetricDisplayName:
                                cutoffMetricDisplayName
                        }
                    })
                    if (
                        createdTutorial.data?.createTrainingTutorial &&
                        !createdTutorial.errors
                    ) {
                        await reloadTutorials()
                        setSampleTutorial(undefined)
                        dispatch(dirtinessChanged(false))
                        return true
                    }
                } else {
                    await updateTutorial({
                        variables: {
                            updateTrainingId: id,
                            updateTrainingCutoffValue: cutoffValue,
                            updateTrainingCutoffOperator: cutoffOperator,
                            updateTrainingCorrectAnswer: tutorialCorrectAnswer
                        }
                    })
                    dispatch(dirtinessChanged(false))
                    return true
                }
            } catch (error) {
                dispatch(failMessage("Failed to create tutorial", error))
            }
            return false
        },
        [
            createTutorial,
            dispatch,
            quaifierName,
            reloadTutorials,
            sampleTutorial,
            updateTutorial
        ]
    )

    return {
        sampleTutorial,
        addSampleTutorial,
        createOrUpdateTutorial,
        isUpdatingTutorial: tutorialCreationLoading || loading
    }
}
